import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  useTheme,
  alpha,
  Chip,
  Divider,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  IconButton,
  Tooltip,
  Alert
} from "@mui/material";
import {
  TrendingUp,
  AttachMoney,
  Receipt,
  Payment,
  CalendarToday,
  ArrowUpward,
  ArrowDownward,
  CreditCard,
  Download,
  Info
} from "@mui/icons-material";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler
} from "chart.js";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, ChartTooltip, Legend, Filler);

const Earnings = () => {
  const theme = useTheme();
  const [period, setPeriod] = useState("weekly"); // weekly, monthly, yearly

  // Mock earnings data
  const totalEarnings = 2847.50;
  const pendingPayout = 342.00;
  const nextPayoutDate = "June 5, 2025";

  // Weekly earnings for chart
  const weeklyEarnings = [320, 450, 380, 520, 410, 580, 490];
  const weeklyLabels = ["Week 1", "Week 2", "Week 3", "Week 4", "Week 5", "Week 6", "Week 7"];

  const monthlyEarnings = [1250, 1580, 1420, 1890];
  const monthlyLabels = ["March", "April", "May", "June"];

  const yearlyEarnings = [4500, 8200, 13400, 21800];
  const yearlyLabels = ["2022", "2023", "2024", "2025"];

  const getChartData = () => {
    switch (period) {
      case "weekly":
        return { labels: weeklyLabels, data: weeklyEarnings };
      case "monthly":
        return { labels: monthlyLabels, data: monthlyEarnings };
      default:
        return { labels: yearlyLabels, data: yearlyEarnings };
    }
  };

  const chartData = getChartData();

  const chartConfig = {
    labels: chartData.labels,
    datasets: [
      {
        label: "Earnings",
        data: chartData.data,
        fill: true,
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        borderColor: theme.palette.primary.main,
        borderWidth: 2,
        tension: 0.3,
        pointBackgroundColor: theme.palette.primary.main,
        pointBorderColor: "#fff",
        pointRadius: 4,
        pointHoverRadius: 6
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: (ctx) => `$${ctx.raw.toFixed(2)}` } }
    },
    scales: {
      y: { ticks: { callback: (val) => `$${val}` }, grid: { color: alpha(theme.palette.divider, 0.1) } },
      x: { grid: { display: false } }
    }
  };

  // Mock transaction history
  const transactions = [
    { id: 1, date: "May 28, 2025", job: "Beach House Cleaning", amount: 125.00, status: "paid", payoutId: "PY-1234" },
    { id: 2, date: "May 25, 2025", job: "Downtown Apartment", amount: 95.50, status: "paid", payoutId: "PY-1233" },
    { id: 3, date: "May 22, 2025", job: "Lakeside Villa", amount: 210.00, status: "paid", payoutId: "PY-1232" },
    { id: 4, date: "May 20, 2025", job: "Modern Loft", amount: 150.00, status: "paid", payoutId: "PY-1231" },
    { id: 5, date: "May 18, 2025", job: "Suburban Home", amount: 88.00, status: "pending", payoutId: null }
  ];

  const getStatusChip = (status) => {
    if (status === "paid") return <Chip label="Paid" size="small" color="success" />;
    return <Chip label="Pending" size="small" color="warning" />;
  };

  const handleDownloadReport = () => {
    alert("Download earnings report (CSV) – demo");
  };

  const handleRequestPayout = () => {
    alert("Payout requested – demo");
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Chip
          label="Cleaner Earning"
          sx={{
            mb: 2,
            background: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.dark,
            fontWeight: 600
          }}
        />
        <Typography variant="h4" fontWeight={700}>Earnings</Typography>
        <Typography variant="body1" color="text.secondary">
          Track your cleaning income, view transaction history, and manage payouts.
        </Typography>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">Total Earnings</Typography>
                <AttachMoney sx={{ color: theme.palette.primary.main }} />
              </Box>
              <Typography variant="h3" fontWeight={700}>${totalEarnings.toFixed(2)}</Typography>
              <Typography variant="caption" color="text.secondary">All time</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">Pending Payout</Typography>
                <Payment sx={{ color: theme.palette.warning.main }} />
              </Box>
              <Typography variant="h3" fontWeight={700}>${pendingPayout.toFixed(2)}</Typography>
              <Typography variant="caption" color="text.secondary">Next payout: {nextPayoutDate}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle2" color="text.secondary">Total Jobs</Typography>
                <Receipt sx={{ color: theme.palette.success.main }} />
              </Box>
              <Typography variant="h3" fontWeight={700}>42</Typography>
              <Typography variant="caption" color="text.secondary">Completed cleanings</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Chart Section */}
      <Paper sx={{ p: 3, borderRadius: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>Earnings Trend</Typography>
          <Tabs value={period} onChange={(e, v) => setPeriod(v)}>
            <Tab label="Weekly" value="weekly" />
            <Tab label="Monthly" value="monthly" />
            <Tab label="Yearly" value="yearly" />
          </Tabs>
        </Box>
        <Box sx={{ height: 280 }}>
          <Line data={chartConfig} options={chartOptions} />
        </Box>
      </Paper>

      {/* Transactions & Payout */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>Recent Transactions</Typography>
              <Tooltip title="Download CSV">
                <IconButton onClick={handleDownloadReport}><Download /></IconButton>
              </Tooltip>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha(theme.palette.divider, 0.05) }}>
                    <TableCell>Date</TableCell>
                    <TableCell>Job</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transactions.map((tx) => (
                    <TableRow key={tx.id} hover>
                      <TableCell>{tx.date}</TableCell>
                      <TableCell>{tx.job}</TableCell>
                      <TableCell align="right">${tx.amount.toFixed(2)}</TableCell>
                      <TableCell>{getStatusChip(tx.status)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button size="small" href="/earnings/history">View All Transactions →</Button>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>Payout Method</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                <CreditCard />
              </Avatar>
              <Box>
                <Typography variant="body2" fontWeight={500}>Visa ending in 1234</Typography>
                <Typography variant="caption" color="text.secondary">Expires 12/27</Typography>
              </Box>
            </Box>
            <Button variant="outlined" size="small" fullWidth sx={{ mb: 2 }}>Change Payout Method</Button>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Payout Schedule</Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Payments are processed weekly on Thursdays. Funds appear in your account within 2‑3 business days.
            </Typography>
            <Button variant="contained" fullWidth onClick={handleRequestPayout} disabled={pendingPayout === 0}>
              Request Early Payout
            </Button>
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="caption">Minimum payout: $20. No fees for standard payouts.</Typography>
            </Alert>
          </Paper>
        </Grid>
      </Grid>

      {/* Additional Stats */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>Earnings Breakdown</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Platform Commission (0%)</Typography>
              <Typography variant="body2" fontWeight={500}>−$0.00</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Payment Processing (2.9% + $0.30)</Typography>
              <Typography variant="body2" fontWeight={500}>−$82.88</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body1" fontWeight={700}>Net Received</Typography>
              <Typography variant="body1" fontWeight={700} color="success.main">$2,764.62</Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>Tips for Higher Earnings</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <TrendingUp color="primary" fontSize="small" />
              <Typography variant="body2">Complete certification – get 35% more booking requests</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ArrowUpward color="primary" fontSize="small" />
              <Typography variant="body2">Maintain 5‑star rating to be featured in search</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday color="primary" fontSize="small" />
              <Typography variant="body2">Accept same‑day requests for bonus pay</Typography>
            </Box>
            <Button variant="text" sx={{ mt: 2 }} href="/training">Go to Training →</Button>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Earnings;